import { useCallback, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { message, Progress, theme } from 'antd';
import { fetchProtectedMedia } from '../utils/protectedMedia';

interface UseAssetDownloadReturn {
  downloadAsset: (url: string, fileName?: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const DOWNLOAD_MESSAGE_KEY = 'asset-download';

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v']);

const getAssetKind = (url: string, fileName?: string): 'video' | 'image' | 'file' => {
  const path = String(fileName || url)
    .split('?')[0]
    .toLowerCase();
  const extension = path.match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toLowerCase();

  if (!extension) {
    return 'file';
  }

  if (VIDEO_EXTENSIONS.has(extension)) {
    return 'video';
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff'].includes(extension)) {
    return 'image';
  }

  return 'file';
};

const getDownloadLabels = (kind: 'video' | 'image' | 'file') => {
  if (kind === 'video') {
    return {
      preparing: 'Preparing video download...',
      downloading: 'Downloading video',
    };
  }

  if (kind === 'image') {
    return {
      preparing: 'Preparing download...',
      downloading: 'Downloading...',
    };
  }

  return {
    preparing: 'Preparing download...',
    downloading: 'Downloading...',
  };
};

const useAssetDownload = (): UseAssetDownloadReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = theme.useToken();

  const showDownloadProgress = useCallback(
    (progress: number | null, kind: 'video' | 'image' | 'file' = 'file') => {
      const labels = getDownloadLabels(kind);

      message.open({
        key: DOWNLOAD_MESSAGE_KEY,
        content:
          progress === null ? (
            <span className="inline-flex items-center gap-2">
              <LoadingOutlined spin style={{ color: token.colorPrimary }} />
              {labels.preparing}
            </span>
          ) : (
            <div className="min-w-52">
              <div className="mb-1 inline-flex items-center gap-2">
                <LoadingOutlined spin style={{ color: token.colorPrimary }} />
                {labels.downloading}
              </div>
              <Progress percent={progress} size="small" status="active" strokeColor={token.colorPrimary} />
            </div>
          ),
        duration: 0,
      });
    },
    [token.colorPrimary]
  );

  const downloadAsset = useCallback(
    async (url: string, fileName?: string) => {
      const assetKind = getAssetKind(url, fileName);

      try {
        setLoading(true);
        setError(null);
        showDownloadProgress(0, assetKind);

        const response = await fetchProtectedMedia(url);

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status}`);
        }

        const contentLength = Number(response.headers.get('content-length') || 0);
        const reader = response.body?.getReader();
        let blob: Blob;

        if (reader) {
          const chunks: Uint8Array[] = [];
          let receivedLength = 0;

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            if (value) {
              chunks.push(value);
              receivedLength += value.length;

              if (contentLength > 0) {
                showDownloadProgress(Math.min(100, Math.round((receivedLength / contentLength) * 100)), assetKind);
              } else {
                showDownloadProgress(null, assetKind);
              }
            }
          }

          const contentType = response.headers.get('content-type') || undefined;
          blob = new Blob(chunks as unknown as BlobPart[], contentType ? { type: contentType } : undefined);
        } else {
          blob = await response.blob();
        }

        showDownloadProgress(100, assetKind);
        const blobUrl = window.URL.createObjectURL(blob);

        // Auto detect filename if not provided
        const resolvedFileName = fileName || url.split('/').pop()?.split('?')[0] || 'download';

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = resolvedFileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);
        message.success({
          key: DOWNLOAD_MESSAGE_KEY,
          content: `${resolvedFileName} has been downloaded.`,
          duration: 3,
        });
      } catch (err: any) {
        console.error('Download failed:', err);
        setError(err.message || 'Download failed');
        message.error({
          key: DOWNLOAD_MESSAGE_KEY,
          content: err.message || 'Download failed',
        });
      } finally {
        setLoading(false);
      }
    },
    [showDownloadProgress]
  );

  return { downloadAsset, loading, error };
};

export default useAssetDownload;
